import {
  ActionIcon,
  Button,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { Controller, useFieldArray, useFormState, type Control } from 'react-hook-form';
import { emptyContact, type SusFormValues } from './susFormSchema';

type Props = {
  control: Control<SusFormValues>;
  disabled: boolean;
};

/** Saisie de la liste des contacts (au moins un), chacun dans un bloc numéroté. */
const SusContactsFields = ({ control, disabled }: Props) => {
  const { t } = useTranslate();
  const { fields, append, remove } = useFieldArray({ control, name: 'contacts' });
  const { errors } = useFormState({ control, name: 'contacts' });

  return (
    <Stack gap="xs">
      {fields.map((contact, index) => (
        <Paper key={contact.id} withBorder p="sm">
          <Group justify="space-between" mb="xs" wrap="nowrap">
            <Text size="sm" fw={500}>
              {t('admin.sus.form.contacts.item', { number: index + 1 })}
            </Text>
            <Tooltip label={t('admin.sus.form.contacts.remove')} withArrow>
              {/* Le span reste actif quand le bouton est désactivé, pour garder l'infobulle. */}
              <span>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={t('admin.sus.form.contacts.remove')}
                  onClick={() => remove(index)}
                  disabled={disabled || fields.length <= 1}
                >
                  <IconTrash size={16} stroke={1.5} />
                </ActionIcon>
              </span>
            </Tooltip>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            <Controller
              control={control}
              name={`contacts.${index}.libelle`}
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  value={field.value ?? ''}
                  label={t('admin.sus.form.contacts.libelle')}
                  error={fieldState.error ? t('admin.sus.form.error.required') : undefined}
                  disabled={disabled}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name={`contacts.${index}.email`}
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  value={field.value ?? ''}
                  type="email"
                  label={t('admin.sus.form.contacts.email')}
                  error={fieldState.error ? t('admin.sus.form.contacts.error.email') : undefined}
                  disabled={disabled}
                  required
                />
              )}
            />
          </SimpleGrid>
        </Paper>
      ))}
      {errors.contacts?.root && (
        <Text size="sm" c="red" role="alert">
          {t('admin.sus.form.contacts.error.min')}
        </Text>
      )}
      <Button
        variant="subtle"
        leftSection={<IconPlus size={16} />}
        onClick={() => append(emptyContact)}
        disabled={disabled}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('admin.sus.form.contacts.add')}
      </Button>
    </Stack>
  );
};

export default SusContactsFields;
